import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ChatIAButton } from '../../components/ChatIA';
import AnalisePredicaoCard from '../../components/saude/AnalisePredicaoCard';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Fade,
  alpha,
  useTheme,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QuadrimestreSelector, { getQuadrimestreInicial, saveQuadrimestre, formatQuadrimestreParam } from '../../components/saude/QuadrimestreSelector';
import FonteDadosSelector, { getFonteInicial, FonteDados } from '../../components/saude/FonteDadosSelector';
import HelpButton from '../../components/saude/HelpButton';
import ClassificacaoBadge, { getClassificacaoColor, getPercentualColor } from '../../components/saude/ClassificacaoBadge';
import TabPanel from '../../components/saude/TabPanel';
import EquipeCard from '../../components/saude/EquipeCard';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import { EIXO_GROUPS } from '../../data/eixo-groups';
import ProgressBarWithMetas from '../../components/saude/ProgressBarWithMetas';
import { buildEixoContext } from '../../utils/saude-context-text';
import { compararEquipesPiorPrimeiro, isEscalaInvertida, C1_FAIXA_IDEAL, C1_THRESHOLDS } from '../../utils/saude-classificacao';
import { focoStatusDefault } from '../../utils/saude-foco';

// Eixos cujos cards de boa pratica sao clicaveis (abrem a lista de faltantes/entraram).
// Ampliar conforme a replicacao do recurso para os demais eixos.
// c1-mais-acesso abre a variante por TIPO DE DEMANDA (Programados x Espontaneos) - ver utils/saude-foco.
const BOA_PRATICA_CLICAVEL = new Set<string>(['c1-mais-acesso', 'c2-criancas', 'c3-gestantes', 'c4-diabeticos', 'c5-hipertensos', 'c6-idosos', 'c7-mulheres', 'b2-tratamento-concluido', 'b3-exodontia', 'b4-escovacao', 'b5-preventivos', 'b6-tra-art', 'm2-acoes-interprofissionais', 'ecr-gestacao', 'ecr-ist', 'ecr-tuberculose']);

// Eixos em que o card de equipe NAO exibe o botao "Ver pacientes desta equipe"
// (decisao do gestor). No c1-mais-acesso a lista e de ATENDIMENTOS, nao de pacientes.
const OCULTAR_VER_PACIENTES_EQUIPE = new Set<string>(['c1-mais-acesso']);

// Eixos em que a visita domiciliar NAO e condicionante para eAP (tipo 76):
// o denominador dessa boa pratica fica restrito as equipes eSF (tipo 70), logo
// menor que o total de identificados. Mostra um informativo no card. (nota MS)
// Se futuramente o C4 for alinhado a mesma regra, basta adicionar 'c4-diabeticos'.
const VISITA_NAO_CONDICIONANTE_EAP = new Set<string>(['c5-hipertensos', 'c6-idosos']);

// Eixos onde a pontuacao ("X pts") e o potencial ("(+Y pts)") dos cards de boa pratica
// ficam OCULTOS (decisao do gestor: nao fazem sentido claro na tela). O codigo de render
// fica preservado e reversivel — para reexibir num eixo, basta remove-lo deste set.
// Ao habilitar a lista por boa pratica num novo eixo, avaliar adiciona-lo aqui.
// Ver docs/GUIA-LISTA-BOA-PRATICA.md (secao "Ocultacao de pontuacao/potencial").
const OCULTAR_PONTUACAO_BOA_PRATICA = new Set<string>(['c1-mais-acesso', 'c2-criancas', 'c3-gestantes', 'c4-diabeticos', 'c5-hipertensos', 'c6-idosos', 'c7-mulheres', 'b1-primeira-consulta', 'b2-tratamento-concluido', 'b3-exodontia', 'b4-escovacao', 'b5-preventivos', 'b6-tra-art', 'm2-acoes-interprofissionais', 'ecr-gestacao', 'ecr-ist', 'ecr-tuberculose']);

/**
 * Texto do valor de um indicador: valor real atingido (% ou razao) quando disponivel
 * (eixos com faixa/banda: eSB, eMulti), senao o percentual.
 */
function formatIndicadorValor(ind: any): string {
  return (ind?.valor !== undefined && ind?.valor !== null)
    ? (ind.unidade === 'ratio'
        ? ind.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : `${ind.valor.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`)
    : `${ind.percentual}%`;
}

const SaudeEixo: React.FC = () => {
  const { eixo } = useParams<{ eixo: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [resumo, setResumo] = useState<any>(null);
  const [resumoPorEquipe, setResumoPorEquipe] = useState<any[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [equipeSearch, setEquipeSearch] = useState('');

  const qAtual = getQuadrimestreInicial();
  const [quadrimestre, setQuadrimestre] = useState(qAtual.quadrimestre);
  const [ano, setAno] = useState(qAtual.ano);
  const [fonte, setFonte] = useState<FonteDados>(getFonteInicial());
  const qParam = formatQuadrimestreParam(quadrimestre, ano);

  // If coming from EquipeCard with ?tab=equipes, start on equipes tab
  useEffect(() => {
    if (searchParams.get('tab') === 'equipes') setTabValue(1);
  }, [searchParams]);

  const handleQuadChange = (q: string, a: number) => { setQuadrimestre(q); setAno(a); saveQuadrimestre(q, a); };

  const fetchData = useCallback(async () => {
    if (!municipioAtivo?.id || !eixo) return;
    setLoading(true); setError(null);
    try {
      const [resumoData, equipeBreakdown] = await Promise.all([
        saudeService.getEixoResumo(eixo, municipioAtivo.id, undefined, qParam, unidadeSelecionada?.codunidade),
        saudeService.getEixoEquipes(eixo, municipioAtivo.id, qParam, unidadeSelecionada?.codunidade),
      ]);
      setResumo(resumoData);
      setResumoPorEquipe(equipeBreakdown);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados do eixo.');
    } finally { setLoading(false); }
    // `fonte` nao aparece no corpo de proposito: ela viaja pelo interceptor do services/api.ts,
    // que a le do sessionStorage. Ainda assim precisa ficar aqui — o efeito abaixo depende da
    // identidade de fetchData, entao sem esta dep alternar a fonte nao recarregaria a tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipioAtivo?.id, eixo, qParam, fonte, unidadeSelecionada?.codunidade]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Indicator nomes map from resumo
  const indicadoresNomes = useMemo(() => {
    const map: Record<string, string> = {};
    (resumo?.indicadores || []).forEach((ind: any) => { map[ind.indicador] = ind.nome; });
    return map;
  }, [resumo]);

  const eixoGroup = useMemo(() => EIXO_GROUPS.find(g => g.eixos.includes(eixo!)), [eixo]);
  const dadosContexto = useMemo(() => {
    if (!resumo) return undefined;
    return buildEixoContext(resumo, resumoPorEquipe);
  }, [resumo, resumoPorEquipe]);

  // Sorted equipes (worst first). No C1 a escala e invertida (>70% tambem e Regular),
  // por isso a ordenacao usa a classificacao + distancia da faixa ideal, e nao o score
  // crescente. Ver utils/saude-classificacao.ts.
  const sortedEquipes = useMemo(() => {
    let filtered = [...resumoPorEquipe];
    if (equipeSearch) {
      const q = equipeSearch.toLowerCase();
      filtered = filtered.filter((eq: any) =>
        eq.equipe?.toLowerCase().includes(q) || eq.unidade?.toLowerCase().includes(q) || eq.ine?.includes(q)
      );
    }
    return filtered.sort(compararEquipesPiorPrimeiro(eixo));
  }, [resumoPorEquipe, equipeSearch, eixo]);

  const escalaInvertida = isEscalaInvertida(eixo);

  const isTaxa = resumo?.tipo === 'taxa';
  // Eixos de taxa normalmente nao tem lista nominal; os eSB (B1-B6) sinalizam tem_lista_nominal
  const mostrarListaNominal = !isTaxa || resumo?.tem_lista_nominal === true;

  if (!municipioAtivo) {
    return (<Box sx={{ p: 3 }}><Alert severity="warning">Selecione um municipio para visualizar os indicadores.</Alert></Box>);
  }
  if (loading) {
    return (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}><CircularProgress /></Box>);
  }
  if (error) {
    return (<Box sx={{ p: 3 }}><Alert severity="error" sx={{ mb: 2 }}>{error}</Alert><Button variant="outlined" onClick={() => navigate(-1)}>Voltar</Button></Box>);
  }
  if (!resumo) {
    return (<Box sx={{ p: 3 }}><Alert severity="info">Nenhum dado encontrado para este eixo.</Alert></Box>);
  }

  const indicators = resumo.indicadores || [];
  // Eixos de indicador unico com faixa (eSB B1-B6, eMulti M1/M2): o card "Score" mostra o valor
  // atingido (ex.: 76,9%) em vez do valor da faixa (score_medio = 100 no "Otimo"), batendo com o card do indicador.
  const indUnicoBandado = indicators.length === 1 && indicators[0]?.valor != null;
  const scoreDisplay = indUnicoBandado ? formatIndicadorValor(indicators[0]) : (resumo.score_medio ?? '-');
  const radarData = indicators.map((ind: any) => ({
    indicador: ind.indicador,
    nome: ind.nome,
    percentual: ind.percentual ?? 0,
    fullMark: 100,
  }));

  return (
    <>
    <Fade in timeout={600}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate('/saude')}
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) } }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="h4" fontWeight={700}>{resumo.nome}</Typography>
              <HelpButton eixo={eixo!} size={18} />
            </Box>
            {eixoGroup && <Typography variant="body2" color="text.secondary">{eixoGroup.label} - {eixoGroup.descricao}</Typography>}
          </Box>
          {municipioAtivo && (
            <FonteDadosSelector municipioId={municipioAtivo.id} fonte={fonte} onChange={setFonte} quadrimestre={formatQuadrimestreParam(quadrimestre, ano)} />
          )}
          <QuadrimestreSelector quadrimestre={quadrimestre} ano={ano} onChange={handleQuadChange} />
        </Box>

        {/* Stat strip */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <GroupsIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{eixo === 'c1-mais-acesso' ? 'Atendimentos' : 'Identificados'}</Typography>
                  <Typography variant="h5" fontWeight={700}>{resumo.total_identificados?.toLocaleString('pt-BR') ?? '-'}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: alpha(getClassificacaoColor(resumo.classificacao), 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PersonIcon sx={{ color: getClassificacaoColor(resumo.classificacao), fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Score</Typography>
                  <Typography variant="h5" fontWeight={700}>{scoreDisplay}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <ClassificacaoBadge classificacao={resumo.classificacao} size="medium" />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant="fullWidth"
            sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
          >
            <Tab label="Visao Geral" id="tab-0" aria-controls="tabpanel-0" />
            <Tab label={`Por Equipe (${resumoPorEquipe.length})`} id="tab-1" aria-controls="tabpanel-1" />
            {mostrarListaNominal && <Tab label="Lista Nominal" id="tab-2" aria-controls="tabpanel-2" />}
          </Tabs>
        </Box>

        {/* Tab 0: Visão Geral */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Indicators */}
            <Grid item xs={12} md={radarData.length >= 3 && radarData.some((d: any) => d.percentual > 0) ? 7 : 12}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>Indicadores</Typography>
              <Grid container spacing={1.5}>
                {indicators.map((ind: any, index: number) => {
                  const faltam = (ind.denominador || 0) - (ind.numerador || 0);
                  // Boa pratica de visita nao condicionante para eAP (tipo 76): base restrita a eSF (tipo 70),
                  // por isso menor que o total de identificados. Exibe informativo no card.
                  const denomRestritoEsf =
                    VISITA_NAO_CONDICIONANTE_EAP.has(eixo!) &&
                    ind.denominador != null &&
                    resumo.total_identificados != null &&
                    ind.denominador < resumo.total_identificados;
                  const potencial = ((100 - (ind.percentual ?? 0)) * (ind.peso ?? 0)) / 100;
                  // Oculta "X pts" (pontuacao) e "(+Y pts)" (potencial) nos eixos configurados.
                  const ocultarPts = OCULTAR_PONTUACAO_BOA_PRATICA.has(eixo!);
                  // Em eixos de TAXA habilitados como boa pratica (ex.: b2), numerador NAO e subconjunto
                  // do denominador, entao "den - num" nao representa os faltantes reais. Oculta o texto
                  // "Faltam N" no card (a contagem correta aparece no toggle da lista, vinda da propria query).
                  // b1 (1a consulta) tambem oculta o "Faltam N" por decisao do gestor, sem virar clicavel.
                  // c1 (Mais Acesso) oculta o "Faltam N" por decisao do gestor (independente do tipo).
                  const ocultarFaltam =
                    eixo === 'c1-mais-acesso' ||
                    (isTaxa && (BOA_PRATICA_CLICAVEL.has(eixo!) || eixo === 'b1-primeira-consulta'));
                  // Nos eixos de escala invertida (C1) a cor vem da classificacao do eixo
                  // (indicador unico cujo percentual e o proprio score), senao 85% sairia
                  // verde ao lado do selo vermelho "Regular".
                  const indColor = ind.classificacao
                    ? getClassificacaoColor(ind.classificacao)
                    : escalaInvertida
                      ? getClassificacaoColor(resumo.classificacao)
                      : getPercentualColor(ind.percentual ?? 0);
                  const valorTexto = formatIndicadorValor(ind);
                  return (
                    <Grid item xs={12} md={indicators.length >= 5 ? 6 : 12} key={ind.indicador || index}>
                      <Card
                        elevation={0}
                        onClick={BOA_PRATICA_CLICAVEL.has(eixo!) ? () => navigate(`/saude/${eixo}/lista?indicador=${ind.indicador}&status=${focoStatusDefault(eixo)}&quadrimestre=${qParam}`) : undefined}
                        sx={{
                          borderRadius: 2, border: `1px solid ${theme.palette.divider}`, height: '100%',
                          ...(BOA_PRATICA_CLICAVEL.has(eixo!)
                            ? { cursor: 'pointer', transition: 'box-shadow .15s, border-color .15s', '&:hover': { boxShadow: 3, borderColor: theme.palette.primary.main } }
                            : {}),
                        }}
                      >
                        <CardContent sx={{ pb: '12px !important', pt: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>{ind.indicador} - {ind.nome}</Typography>
                                <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
                                  <HelpButton eixo={eixo!} indicador={ind.indicador} size={14} />
                                </Box>
                              </Box>
                              {faltam > 0 && !ocultarFaltam && (
                                <Typography variant="caption" sx={{ color: '#e65100' }}>
                                  Faltam {faltam.toLocaleString('pt-BR')} cidadaos
                                  {potencial > 2 && !ocultarPts && <Typography component="span" sx={{ color: '#1565c0', fontWeight: 600 }}> (+{potencial.toFixed(1)} pts)</Typography>}
                                </Typography>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0 }}>
                              <Typography variant="caption" color="text.secondary">Peso {ind.peso}</Typography>
                              {!ocultarPts && (
                                <Typography variant="body2" fontWeight={700} sx={{ color: indColor, minWidth: 36, textAlign: 'right' }}>
                                  {ind.pontuacao} pts
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ flex: 1 }}>
                              <ProgressBarWithMetas value={ind.percentual ?? 0} color={indColor} height={8} thresholds={escalaInvertida ? C1_THRESHOLDS : undefined} />
                            </Box>
                            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 42, textAlign: 'right' }}>{valorTexto}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {ind.numerador?.toLocaleString('pt-BR')} / {ind.denominador?.toLocaleString('pt-BR')}
                              </Typography>
                              {denomRestritoEsf && (
                                <MuiTooltip
                                  arrow
                                  title="Base restrita as equipes eSF (tipo 70). A visita domiciliar nao e condicionante para eAP (tipo 76), conforme a nota metodologica do MS - por isso o total desta boa pratica e menor que o de identificados."
                                >
                                  <Box component="span" onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex', cursor: 'help' }}>
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  </Box>
                                </MuiTooltip>
                              )}
                            </Box>
                            {ind.classificacao && <ClassificacaoBadge classificacao={ind.classificacao} variant="text" size="small" />}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            {/* Radar chart */}
            {radarData.length >= 3 && radarData.some((d: any) => d.percentual > 0) && (
              <Grid item xs={12} md={5}>
                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, position: 'sticky', top: 80 }}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Radar de Indicadores</Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <RadarChart data={radarData} outerRadius={100}>
                        <PolarGrid stroke={alpha(theme.palette.text.primary, 0.1)} />
                        {/* @ts-ignore recharts type compatibility */}
                        <PolarAngleAxis dataKey="indicador" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 10 }} />
                        <Radar dataKey="percentual" stroke={theme.palette.primary.main} fill={alpha(theme.palette.primary.main, 0.2)} fillOpacity={0.6} />
                        <Tooltip formatter={(value: any) => [`${value}%`, 'Cumprimento']} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Tab 1: Por Equipe */}
        <TabPanel value={tabValue} index={1}>
          {/* Search */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Buscar equipe..."
              value={equipeSearch}
              onChange={(e) => setEquipeSearch(e.target.value)}
              sx={{ minWidth: 250 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            {escalaInvertida ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {sortedEquipes.length} equipes ordenadas por classificacao e distancia da faixa ideal
                  ({'>'}{C1_FAIXA_IDEAL.min}% a {C1_FAIXA_IDEAL.max}%) &mdash; pior primeiro
                </Typography>
                <MuiTooltip
                  arrow
                  title={`Neste indicador percentual maior nao e melhor: acima de ${C1_FAIXA_IDEAL.max}% de demanda programada a equipe volta a ser classificada como Regular, conforme a nota metodologica do MS. As equipes mais distantes da faixa ideal aparecem primeiro.`}
                >
                  <Box component="span" sx={{ display: 'inline-flex', cursor: 'help' }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </Box>
                </MuiTooltip>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {sortedEquipes.length} equipes ordenadas por score (pior primeiro)
              </Typography>
            )}
          </Box>

          {/* EquipeCards */}
          {sortedEquipes.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Nenhuma equipe encontrada.</Typography>
          ) : (
            sortedEquipes.map((eq: any) => (
              <EquipeCard
                key={eq.ine}
                equipe={eq}
                eixo={eixo}
                indicadoresNomes={indicadoresNomes}
                onVerPacientes={
                  OCULTAR_VER_PACIENTES_EQUIPE.has(eixo || '')
                    ? undefined
                    : (ine) => navigate(`/saude/${eixo}/lista?ine=${ine}`)
                }
              />
            ))
          )}
        </TabPanel>

        {/* Tab 2: Lista Nominal (inline) */}
        {mostrarListaNominal && (
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(`/saude/${eixo}/lista`)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4 }}
              >
                Abrir Lista Nominal Completa
              </Button>
            </Box>
          </TabPanel>
        )}
      </Box>
    </Fade>
    {municipioAtivo && dadosContexto && (
      <AnalisePredicaoCard
        dadosContexto={dadosContexto}
        municipioId={municipioAtivo.id}
        painelId={`eixo-${eixo}`}
        quadrimestre={quadrimestre}
        ano={ano}
      />
    )}
    <ChatIAButton contexto="saude" eixo={eixo} dadosContexto={dadosContexto} fabSx={{ bottom: 24, right: 24, top: 'auto' }} />
    </>
  );
};

export default SaudeEixo;
